import {
  rollTotal,
  type BaccaratOutcome,
  type BaccaratSideBetId,
  type CalloutMode,
  type CrapsRoll,
  type CrapsRollEvent,
  type GameType,
  type HoldemStreet,
  type PartyMode,
  type Phase,
  type PublicHoldemResult,
  type Rank,
  type RoomState,
  type RoulettePocket,
  type SideBetId,
} from '@tv-casino/shared';
import baccarat from '../../../../phrases/baccarat.json';
import blackjack from '../../../../phrases/blackjack.json';
import common from '../../../../phrases/common.json';
import craps from '../../../../phrases/craps.json';
import holdem from '../../../../phrases/holdem.json';
import keno from '../../../../phrases/keno.json';
import party from '../../../../phrases/party.json';
import roulette from '../../../../phrases/roulette.json';
import threecard from '../../../../phrases/threecard.json';
import { DICE_THROW_MS } from '@tv-casino/viewmodels/components/crapsThrow.js';
import { KENO_REVEAL_MS } from '@tv-casino/viewmodels/components/kenoBoardModel.js';

/**
 * The TV dealer's call-outs: "Yo-leven!", "Seven out. Line away.",
 * "Seventeen, black", "Dealer busts". Same shape as `soundCues.ts` — a pure
 * snapshot, a pure diff between two of them, and a thin hook
 * (`useDealerCallouts`) that hands the result to `voice.ts` and the caption.
 * Every judgement lives here.
 *
 * A call is a list of recorded clips from `phrases/`, played back to back,
 * and the caption that goes with them. Cast devices have no speech synthesis
 * worth hearing, so a call with no clip loaded is a caption, never a robot.
 *
 * Every felt is called: craps, roulette, blackjack, baccarat, three-card,
 * Hold'em and keno, plus the table stock they all share.
 */
export interface Callout {
  /** Dedupe key; a repeated broadcast never repeats a call. "craps:3:7" */
  key: string;
  /** Clip ids, `<game>/<id>`, played back to back. Every id exists in `phrases/`. */
  clips: string[];
  /** Caption text. */
  text: string;
  /** high cuts the queue (decisions); low is dropped if anything is waiting. */
  priority: 'low' | 'normal' | 'high';
  /** Called in `essential` mode as well as `full`. */
  essential: boolean;
  /** Hold the call until the matching effect lands (the ball drop, the dice). */
  delayMs?: number;
}

const SCRIPTS = [common, craps, roulette, blackjack, baccarat, holdem, threecard, keno, party];

/** Every line this module can say, by clip id. */
export const PHRASE_TEXT: Readonly<Record<string, string>> = Object.fromEntries(
  SCRIPTS.flatMap((s) => s.phrases.map((p) => [`${s.game}/${p.id}`, p.text] as const)),
);

const say = (clips: string[]): string => clips.map((id) => PHRASE_TEXT[id] ?? '').join(' ');

const call = (
  key: string,
  clips: string[],
  priority: Callout['priority'],
  essential: boolean,
  delayMs?: number,
): Callout => ({ key, clips, text: say(clips), priority, essential, ...(delayMs ? { delayMs } : {}) });

/** The pocket is called once the ball has dropped and the chips are heard. */
export const POCKET_CALL_MS = 420;
/** The roll is called once the dice have landed in the dome. */
export const ROLL_CALL_MS = DICE_THROW_MS + 120;
/** "Last call" and the shooter nudge go out this long before the clock runs out. */
export const CLOCK_CALL_MS = 5_000;
/**
 * Keno's twenty balls land over one reveal; "That's the draw." waits until the
 * board has nearly finished so it does not trample the numbers.
 */
export const KENO_TAIL_MS = Math.max(0, KENO_REVEAL_MS - 600);
/** How many of the twenty drawn numbers get spoken; the board shows the rest. */
export const KENO_CALLED_NUMBERS = 5;

/** The table stock every game loads, whatever else it calls. */
const STOCK = ['common/place_your_bets', 'common/last_call', 'common/no_more_bets', 'common/shuffling_up'];

/** Keno calls the numbers, not the betting window's stock line. */
const KENO_STOCK = [
  ...STOCK.filter((id) => id !== 'common/place_your_bets'),
  'common/pay_the_winners',
  'common/no_winners',
];
const KENO_NUMBERS = Array.from({ length: 80 }, (_, i) => `common/n_${i + 1}`);
/** Hold'em board reads speak ranks three through ten as plain numbers. */
const HOLDEM_NUMBER_TOKENS = Array.from({ length: 8 }, (_, i) => `common/n_${i + 3}`);
/** The winning-hand labels Hold'em borrows from the common script. */
const HAND_CLIPS = [
  'common/hand_high_card',
  'common/hand_pair',
  'common/hand_straight',
  'common/hand_flush',
  'common/hand_three_of_a_kind',
  'common/hand_straight_flush',
];

/**
 * The switch lines, one per game. Every set carries all of them: the line for
 * the new felt has to be decoded before the swap lands, and the old set is
 * what is loaded when it does.
 */
const SWITCH_CLIPS = party.phrases.filter((p) => p.id.startsWith('now_')).map((p) => `party/${p.id}`);

/** Party mode's own lines, loaded on top of the game's set while a party runs. */
export const PARTY_CLIPS: readonly string[] = party.phrases
  .filter((p) => !p.id.startsWith('now_'))
  .map((p) => `party/${p.id}`);

const switchClip = (game: GameType): string => `party/now_${game}`;

/** Every clip a game can call, so `voice.ts` loads that set and nothing more. */
export function gameClips(game: GameType): string[] {
  return [...ownGameClips(game), ...SWITCH_CLIPS];
}

function ownGameClips(game: GameType): string[] {
  const own = (script: { game: string; phrases: { id: string }[] }) =>
    script.phrases.map((p) => `${script.game}/${p.id}`);
  switch (game) {
    case 'craps':
      return [...STOCK, 'common/n_7', ...own(craps)];
    case 'roulette':
      return [...STOCK, ...own(roulette)];
    case 'blackjack':
      return [...STOCK, ...own(blackjack)];
    case 'baccarat':
      return [...STOCK, ...own(baccarat)];
    case 'holdem':
      return [...STOCK, ...HOLDEM_NUMBER_TOKENS, ...HAND_CLIPS, ...own(holdem)];
    case 'threecard':
      return [...STOCK, ...own(threecard)];
    case 'keno':
      return [...KENO_STOCK, ...KENO_NUMBERS, ...own(keno)];
  }
}

export interface CalloutSnapshot {
  /** When this snapshot was taken, on this screen's clock. */
  at: number;
  gameType: GameType;
  phase: Phase;
  round: number;
  shoe: number;
  /** Seats somebody has taken, for the first "Welcome to the table." */
  seated: number;
  /** The live countdown, when there is one. */
  clock: { kind: 'bet' | 'action'; endsAt: number } | null;
  craps: {
    rollNumber: number;
    roll: CrapsRoll | null;
    event: CrapsRollEvent | null;
    /** A come-out twelve that pushed a don't pass on the layout. */
    barTwelve: boolean;
  } | null;
  roulette: {
    spinNumber: number;
    pocket: RoulettePocket | null;
    /** The ball found the pocket it found last spin. */
    repeat: boolean;
  } | null;
  blackjack: {
    /** The dealer's upcard rank, once it has landed face up. */
    upcard: Rank | null;
    revealed: boolean;
    /** The peek turned over a dealer natural. */
    blackjack: boolean;
    /** The dealer's visible total; the whole hand once revealed. */
    total: number | null;
    /** Hands across the table, by how they fared, for the flips. */
    naturals: number;
    busts: number;
    surrenders: number;
    doubles: number;
    hands: number;
    /** Side bets that paid this round, by id. */
    sideWins: SideBetId[];
  } | null;
  baccarat: {
    playerCards: number;
    bankerCards: number;
    playerTotal: number | null;
    bankerTotal: number | null;
    outcome: BaccaratOutcome | null;
    natural: boolean;
    /** Pair side bets that paid this coup, by id. */
    pairs: BaccaratSideBetId[];
  } | null;
  holdem: {
    street: HoldemStreet | null;
    /** Board ranks, face up in draw order. */
    board: (Rank | null)[];
    /** Who last did what, for the action calls. */
    actionKey: string | null;
    action: string | null;
    /** Seats still holding cards this hand. */
    active: number;
    /** A seat has turned its cards up. */
    revealed: boolean;
    results: PublicHoldemResult[] | null;
    wonByFold: boolean;
  } | null;
  threecard: {
    dealerQualifies: boolean | null;
    dealerLabel: string | null;
    /** Pair Plus wagers that paid this hand. */
    pairPlusWon: number;
    /** A played hand earned the ante bonus. */
    bonusPaid: boolean;
    /** Every seat that played lost to the dealer. */
    dealerHeld: boolean;
    settled: boolean;
  } | null;
  keno: {
    /** The twenty numbers in draw order, or null before the draw. */
    draw: number[] | null;
    /** A ticket caught every spot it picked. */
    solid: boolean;
    settled: boolean;
    /** At least one slate came back more than it staked. */
    paid: boolean | null;
  } | null;
  party: {
    mode: PartyMode;
    slot: number;
    lap: number;
    /** No slot after this one. */
    last: boolean;
    roundsPerGame: number | null;
    roundsPlayed: number | null;
    slotEndsAt: number | null;
    endsAt: number | null;
    due: boolean;
    done: boolean;
  } | null;
}

export function calloutSnapshot(state: RoomState | null, at = Date.now()): CalloutSnapshot | null {
  if (!state) return null;
  const d = state.deadline;
  const c = state.gameType === 'craps' ? state.craps : null;
  const r = state.gameType === 'roulette' ? state.roulette : null;
  const bj = state.gameType === 'blackjack' ? state : null;
  const b = state.gameType === 'baccarat' ? state.baccarat : null;
  const h = state.gameType === 'holdem' ? state.holdem : null;
  const t = state.gameType === 'threecard' ? state.threecard : null;
  const k = state.gameType === 'keno' ? state.keno : null;
  const hands = bj?.seats.flatMap((s) => s.hands) ?? [];
  const dealerUp = bj?.dealer.cards[0];
  const pairPlusBySeat = new Map(
    (t?.seats ?? []).map((s) => [s.index, s.pairPlus?.payout ?? 0] as const),
  );
  const pt = state.party;
  return {
    at,
    gameType: state.gameType,
    phase: state.phase,
    round: state.round,
    shoe: state.shoeRemaining,
    seated: state.seats.filter((s) => s.playerId !== null).length,
    clock: d && (d.kind === 'bet' || d.kind === 'action') ? { kind: d.kind, endsAt: d.endsAt } : null,
    craps: c
      ? {
          rollNumber: c.rollNumber,
          roll: c.lastRoll,
          event: c.lastEvent,
          barTwelve: c.lastSettlements.some((s) => s.betId === 'dontPass' && s.resolution === 'push'),
        }
      : null,
    roulette: r
      ? {
          spinNumber: r.spinNumber,
          pocket: r.pocket,
          repeat: r.pocket !== null && r.history.length >= 2 && r.history.at(-2) === r.pocket,
        }
      : null,
    blackjack: bj
      ? {
          upcard: dealerUp?.faceUp ? dealerUp.card.rank : null,
          revealed: bj.dealer.revealed,
          blackjack: bj.dealer.blackjack,
          total: bj.dealer.visibleValue,
          naturals: hands.filter((hand) => hand.status === 'blackjack').length,
          busts: hands.filter((hand) => hand.status === 'busted').length,
          surrenders: hands.filter((hand) => hand.status === 'surrendered').length,
          doubles: hands.filter((hand) => hand.doubled).length,
          hands: hands.length,
          sideWins: bj.seats
            .flatMap((s) => s.sideBets)
            .filter((bet) => (bet.payout ?? 0) > 0)
            .map((bet) => bet.id),
        }
      : null,
    baccarat: b
      ? {
          playerCards: b.playerCards.length,
          bankerCards: b.bankerCards.length,
          playerTotal: b.playerTotal,
          bankerTotal: b.bankerTotal,
          outcome: b.outcome,
          natural: b.natural,
          pairs: b.seats
            .flatMap((s) => s.sideBets)
            .filter((bet) => (bet.payout ?? 0) > 0)
            .map((bet) => bet.id),
        }
      : null,
    holdem: h
      ? {
          street: h.street,
          board: h.board.map((card) => (card.faceUp ? card.card.rank : null)),
          actionKey: h.lastAction
            ? `${h.lastAction.seatIndex}:${h.lastAction.action}:${h.lastAction.amount}`
            : null,
          action: h.lastAction?.action ?? null,
          active: h.seats.filter((s) => s.inHand && !s.folded).length,
          revealed: h.seats.some((s) => s.revealed),
          results: h.results,
          wonByFold: h.wonByFold,
        }
      : null,
    threecard: t
      ? {
          dealerQualifies: t.dealerQualifies,
          dealerLabel: t.dealerLabel,
          pairPlusWon: t.seats.filter((s) => (s.pairPlus?.payout ?? 0) > 0).length,
          bonusPaid: (t.results ?? []).some((result) => {
            if (result.outcome === 'fold') return false;
            // The main payout is the ante's return, the play's and the bonus
            // on top; take the first two back out at their table odds.
            const base =
              t.dealerQualifies === false
                ? result.ante * 2 + result.play
                : result.outcome === 'win'
                  ? (result.ante + result.play) * 2
                  : result.outcome === 'push'
                    ? result.ante + result.play
                    : 0;
            return result.payout - (pairPlusBySeat.get(result.seatIndex) ?? 0) - base > 0;
          }),
          dealerHeld:
            t.results !== null &&
            t.results.some((result) => result.outcome !== 'fold') &&
            t.results.every((result) => result.outcome !== 'win'),
          settled: t.results !== null,
        }
      : null,
    keno: k
      ? {
          draw: k.draw,
          solid: k.seats.some((s) =>
            s.tickets.some(
              (ticket) => ticket.spots.length > 0 && ticket.catches?.length === ticket.spots.length,
            ),
          ),
          settled: k.results !== null,
          paid: k.results === null ? null : k.results.some((result) => result.payout > result.stake),
        }
      : null,
    party: pt
      ? {
          mode: pt.mode,
          slot: pt.slot,
          lap: pt.lap,
          last: pt.next === null,
          roundsPerGame: pt.roundsPerGame,
          roundsPlayed: pt.roundsPlayed,
          slotEndsAt: pt.slotEndsAt,
          endsAt: pt.endsAt,
          due: pt.due,
          done: pt.done,
        }
      : null,
  };
}

const DEALT: readonly Phase[] = ['insurance', 'playing', 'dealer', 'preflop', 'rolling'];

/**
 * The calls one broadcast earns, in the order they should sound, already
 * filtered to the host's mode. The first snapshot only primes: a TV that
 * pairs mid-hand does not call the hand so far. A game swap earns the switch
 * line and nothing from the felt it lands on.
 */
export function dealerCallouts(
  prev: CalloutSnapshot | null,
  next: CalloutSnapshot | null,
  mode: CalloutMode,
): Callout[] {
  if (mode === 'off' || !prev || !next) return [];
  if (prev.gameType !== next.gameType) return [switchCall(prev, next, mode)];
  const calls: Callout[] = [...partyCalls(prev, next)];
  const opened = prev.phase !== next.phase;

  if (next.seated === 1 && prev.seated === 0) {
    calls.push(call('welcome', ['common/welcome'], 'low', false));
  }

  if (next.shoe > prev.shoe) calls.push(call(`shuffle:${next.round}`, ['common/shuffling_up'], 'low', false));

  if (opened && next.phase === 'betting') {
    const open = next.gameType === 'keno' ? 'keno/tickets_open' : 'common/place_your_bets';
    // The session's first window gets a welcome on top of the open.
    const clips = next.round === 1 ? ['common/good_luck', open] : [open];
    calls.push(call(`bets:${next.round}`, clips, 'low', false));
  }

  if (opened && prev.phase === 'betting' && DEALT.includes(next.phase)) {
    if (next.gameType === 'craps') {
      calls.push(call(`comingout:${next.round}`, ['craps/coming_out'], 'normal', false));
    } else if (next.gameType === 'keno') {
      calls.push(call(`nomore:${next.round}`, ['keno/drawing_now'], 'normal', true));
    } else {
      const clips = ['common/no_more_bets'];
      if (next.gameType === 'roulette' && mode === 'full') clips.push('roulette/ball_in');
      calls.push(call(`nomore:${next.round}`, clips, 'normal', true));
    }
  }

  if (next.gameType === 'craps') calls.push(...crapsCalls(prev, next, mode));
  if (next.gameType === 'roulette') calls.push(...rouletteCalls(prev, next, mode));
  if (next.gameType === 'blackjack') calls.push(...blackjackCalls(prev, next, mode));
  if (next.gameType === 'baccarat') calls.push(...baccaratCalls(prev, next, mode));
  if (next.gameType === 'holdem') calls.push(...holdemCalls(prev, next, mode));
  if (next.gameType === 'threecard') calls.push(...threecardCalls(prev, next, mode));
  if (next.gameType === 'keno') calls.push(...kenoCalls(prev, next, mode));

  return mode === 'full' ? calls : calls.filter((c) => c.essential);
}

/**
 * A slot left with time on its clock, or rounds still to deal, was skipped by
 * the host. The margin keeps a switch that landed on time — give or take the
 * gap between the server's clock and this screen's — from reading as one.
 */
const SKIP_MARGIN_MS = 5_000;

const skipped = (prev: CalloutSnapshot, next: CalloutSnapshot): boolean => {
  const p = prev.party;
  if (!p || !next.party || next.party.slot <= p.slot) return false;
  if (p.mode === 'rounds') return (p.roundsPlayed ?? 0) < (p.roundsPerGame ?? 0);
  return !p.due && p.slotEndsAt !== null && p.slotEndsAt - next.at > SKIP_MARGIN_MS;
};

/**
 * The switch line the splash card rides on: "Now dealing blackjack." A party
 * says what moved it — starting, a skip, a new lap — and flags its last game.
 */
function switchCall(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout {
  const p = next.party;
  const started = p !== null && prev.party === null;
  const clips: string[] = [];
  if (started) clips.push('party/party_started');
  else if (mode === 'full' && skipped(prev, next)) clips.push('party/party_skipped');
  clips.push(switchClip(next.gameType));
  if (p && !started && prev.party && p.lap > prev.party.lap && mode === 'full') clips.push('party/next_lap');
  if (p?.mode === 'schedule' && p.last && !p.done) clips.push('party/last_game');
  return call(`switch:${next.gameType}:${next.at}`, clips, 'high', true);
}

/** Party mode on a felt that stays put: on, off, the last round, time up, the end. */
function partyCalls(prev: CalloutSnapshot, next: CalloutSnapshot): Callout[] {
  const p = next.party;
  const was = prev.party;
  const calls: Callout[] = [];

  // A party that opens on the game already dealing: no swap to announce it.
  if (p && !was) {
    calls.push(call(`party:on:${next.at}`, ['party/party_started', switchClip(next.gameType)], 'high', true));
    return calls;
  }
  if (!p) {
    if (was && !was.done) calls.push(call(`party:off:${next.at}`, ['party/party_stopped'], 'normal', true));
    return calls;
  }
  if (!was) return calls;

  if (
    p.mode === 'rounds' &&
    (p.roundsPerGame ?? 0) > 1 &&
    prev.phase !== 'betting' &&
    next.phase === 'betting' &&
    p.roundsPlayed === p.roundsPerGame
  ) {
    calls.push(call(`party:lastround:${p.slot}`, ['party/last_round_here'], 'low', false));
  }
  // Rounds mode is due for the whole last round; only a clock runs out mid-hand.
  if (p.mode === 'schedule' && p.due && !was.due && !p.done) {
    calls.push(call(`party:due:${p.slot}`, ['party/switching_after_this_hand'], 'normal', true));
  }
  if (p.done && !was.done) calls.push(call(`party:over:${p.slot}`, ['party/party_over'], 'high', true));
  return calls;
}

/** "Five minutes left on this game." is not worth saying as the game starts. */
const PARTY_WARNING_LEAD_MS = 30_000;
export const GAME_WARNING_MS = 5 * 60_000;
export const PARTY_WARNING_MS = 10 * 60_000;

/**
 * The calls a party's clock earns before it runs out: five minutes left on
 * this game, ten left in the party. `full` only, like `clockCallout`, and
 * only while the moment is still ahead.
 */
export function partyClockCallouts(
  next: CalloutSnapshot | null,
  mode: CalloutMode,
  now: number,
): { callout: Callout; at: number }[] {
  const p = next?.party;
  if (mode !== 'full' || !p || p.mode !== 'schedule' || p.due || p.done) return [];
  const due: { callout: Callout; at: number }[] = [];
  if (p.slotEndsAt !== null) {
    const at = p.slotEndsAt - GAME_WARNING_MS;
    if (at - now > PARTY_WARNING_LEAD_MS) {
      due.push({ callout: call(`party:5m:${p.slotEndsAt}`, ['party/five_minutes_this_game'], 'low', false), at });
    }
  }
  if (p.endsAt !== null) {
    const at = p.endsAt - PARTY_WARNING_MS;
    if (at - now > PARTY_WARNING_LEAD_MS) {
      due.push({ callout: call(`party:10m:${p.endsAt}`, ['party/ten_minutes_left'], 'normal', false), at });
    }
  }
  return due;
}

/** The stickman's call for one roll: the dice, then what they decided. */
function crapsCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const c = next.craps;
  if (!c?.roll || !c.event) return [];
  const before = prev.round === next.round ? prev.craps?.rollNumber ?? 0 : 0;
  if (c.rollNumber <= before) return [];
  const key = `craps:${next.round}:${c.rollNumber}`;
  const total = rollTotal(c.roll);
  const pair = c.roll[0] === c.roll[1];

  // A seven-out is the whole call; nobody wants "seven" said first.
  if (c.event === 'seven_out') return [call(key, ['craps/seven_out'], 'high', true, ROLL_CALL_MS)];

  const roll =
    total === 7
      ? 'common/n_7'
      : [4, 6, 8, 10].includes(total)
        ? `craps/roll_${total}_${pair ? 'hard' : 'easy'}`
        : `craps/roll_${total}`;
  switch (c.event) {
    case 'natural':
      return [call(key, [roll, 'craps/winner_front_line'], 'high', true, ROLL_CALL_MS)];
    case 'craps': {
      const clips = [roll, 'craps/take_the_line'];
      if (c.barTwelve && mode === 'full') clips.push('craps/bar_twelve');
      return [call(key, clips, 'high', true, ROLL_CALL_MS)];
    }
    case 'point_set':
      return [call(key, [roll, `craps/point_is_${total}`], 'high', true, ROLL_CALL_MS)];
    case 'point_made':
      return [call(key, [roll, 'craps/point_made'], 'high', true, ROLL_CALL_MS)];
    case 'point_roll':
      // A seven here is a seven-out, so the roll clip always exists.
      return [call(key, [roll], 'normal', false, ROLL_CALL_MS)];
  }
}

/** The pocket, once the ball has dropped: "Seventeen, black." */
function rouletteCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const r = next.roulette;
  if (prev.phase !== 'dealer' || next.phase !== 'payout' || r?.pocket == null) return [];
  const clips = [`roulette/pocket_${r.pocket}`];
  if (r.repeat && mode === 'full') clips.push('roulette/same_number');
  return [call(`pocket:${r.spinNumber}`, clips, 'high', true, POCKET_CALL_MS)];
}

/** The upcard's clip id: tens and faces share one, deuce through nine count. */
const upcardClip = (rank: Rank): string =>
  rank === 'A'
    ? 'blackjack/dealer_shows_ace'
    : rank === '10' || rank === 'J' || rank === 'Q' || rank === 'K'
      ? 'blackjack/dealer_shows_ten'
      : `blackjack/dealer_shows_${rank}`;

/** The dealer peeks — and may announce the check — on a ten or an ace. */
const peeksUp = (rank: Rank | null): boolean => rank === 'A' || rank === '10' || rank === 'J' || rank === 'Q' || rank === 'K';

/** The dealer's final read, once the hole card is up and the drawing is done. */
const dealerSettleClip = (total: number | null): string | null => {
  if (total !== null && total > 21) return 'blackjack/dealer_busts';
  if (total !== null && total >= 17) return `blackjack/dealer_has_${total}`;
  // An uncontested hand leaves the dealer short and nobody listening.
  return null;
};

/** The blackjack dealer: the upcard, the seats' fates, the peek, the draw. */
function blackjackCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const b = next.blackjack;
  const p = prev.blackjack;
  if (!b || !p) return [];
  const calls: Callout[] = [];

  if (b.upcard && !p.upcard) {
    calls.push(call(`up:${next.round}`, [upcardClip(b.upcard)], 'low', false));
  }
  if (b.naturals > p.naturals) {
    calls.push(call(`bj:${next.round}:${b.naturals}`, ['blackjack/blackjack'], 'normal', true));
  }
  if (b.busts > p.busts) {
    calls.push(call(`bust:${next.round}:${b.busts}`, ['blackjack/bust'], 'normal', true));
  }
  if (b.surrenders > p.surrenders) {
    calls.push(call(`sur:${next.round}:${b.surrenders}`, ['blackjack/surrender'], 'normal', true));
  }
  if (mode === 'full' && b.doubles > p.doubles) {
    calls.push(call(`dbl:${next.round}:${b.doubles}`, ['blackjack/double_down'], 'normal', false));
  }
  if (mode === 'full' && b.hands > p.hands && next.phase === 'playing') {
    calls.push(call(`split:${next.round}:${b.hands}`, ['blackjack/split'], 'normal', false));
  }

  if (next.phase === 'insurance' && prev.phase !== 'insurance') {
    calls.push(call(`ins:${next.round}`, ['blackjack/insurance_open'], 'normal', true));
    if (b.naturals > 0) {
      calls.push(call(`em:${next.round}`, ['blackjack/even_money'], 'low', false));
    }
  }
  if (b.blackjack && !p.blackjack) {
    // The peek found the natural; the round is over before anyone acts.
    calls.push(call(`dlr:${next.round}`, ['blackjack/dealer_blackjack'], 'high', true));
  } else if (prev.phase === 'insurance' && next.phase === 'playing') {
    const clips = mode === 'full' ? ['blackjack/insurance_closed', 'blackjack/dealer_checks', 'blackjack/nobody_home'] : [];
    if (clips.length) calls.push(call(`peek:${next.round}`, clips, 'low', false));
  } else if (
    mode === 'full' &&
    prev.phase !== 'playing' &&
    next.phase === 'playing' &&
    prev.phase !== 'insurance' &&
    peeksUp(b.upcard)
  ) {
    // Insurance was off, but a ten or an ace up still earns the check.
    calls.push(call(`peek:${next.round}`, ['blackjack/dealer_checks', 'blackjack/nobody_home'], 'low', false));
  }

  if (next.phase === 'dealer' && prev.phase === 'playing') {
    calls.push(call(`turn:${next.round}`, ['blackjack/dealer_turn'], 'low', false));
  }
  if (prev.phase === 'dealer' && next.phase === 'payout' && b.revealed && !b.blackjack) {
    const clip = dealerSettleClip(b.total);
    if (clip) calls.push(call(`dlr:${next.round}`, [clip], 'high', true));
  }

  if (mode === 'full') {
    for (const id of b.sideWins) {
      if (p.sideWins.includes(id)) continue;
      calls.push(call(`sb:${next.round}:${id}`, [`blackjack/${id}_winner`], 'normal', false));
    }
  }

  return calls;
}

/** Which side (if either) opened a two-card natural, from the visible hands. */
const naturalSides = (b: NonNullable<CalloutSnapshot['baccarat']>): string[] => {
  const sides: string[] = [];
  if (b.playerCards === 2 && (b.playerTotal ?? 0) >= 8) sides.push(`baccarat/player_natural_${b.playerTotal}`);
  if (b.bankerCards === 2 && (b.bankerTotal ?? 0) >= 8) sides.push(`baccarat/banker_natural_${b.bankerTotal}`);
  return sides;
};

/** The baccarat caller: the coup, from first natural to final total. */
function baccaratCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const b = next.baccarat;
  const p = prev.baccarat;
  if (!b || !p) return [];
  const calls: Callout[] = [];

  if (mode === 'full' && next.phase === 'dealer') {
    const drewPlayer = b.playerCards > p.playerCards && b.playerCards > 2;
    const drewBanker = b.bankerCards > p.bankerCards && b.bankerCards > 2;
    if (drewPlayer || drewBanker) {
      const clips = [
        ...(drewPlayer ? ['baccarat/card_for_player'] : []),
        ...(drewBanker ? ['baccarat/card_for_banker'] : []),
      ];
      calls.push(call(`draw:${next.round}:${b.playerCards}-${b.bankerCards}`, clips, 'normal', false));
    }
  }

  if (b.natural && !p.natural) {
    const sides = naturalSides(b);
    if (sides.length) calls.push(call(`natural:${next.round}`, sides, 'high', true));
  }

  if (b.outcome !== null && p.outcome === null) {
    const clips: string[] = [];
    if (b.playerTotal !== null) clips.push(`baccarat/player_${b.playerTotal}`);
    if (b.bankerTotal !== null) clips.push(`baccarat/banker_${b.bankerTotal}`);
    if (mode === 'full' && !b.natural) {
      // Who stood pat: the hands that never took a third card.
      if (b.playerCards === 2) clips.push('baccarat/player_stands');
      if (b.bankerCards === 2) clips.push('baccarat/banker_stands');
    }
    clips.push(
      b.outcome === 'player'
        ? 'baccarat/player_wins'
        : b.outcome === 'banker'
          ? 'baccarat/banker_wins'
          : 'baccarat/tie_hand',
    );
    calls.push(call(`coup:${next.round}`, clips, 'high', true));
  }

  if (mode === 'full') {
    for (const id of b.pairs) {
      if (p.pairs.includes(id)) continue;
      calls.push(call(`pair:${next.round}:${id}`, [`baccarat/${id}_winner`], 'normal', false));
    }
  }

  return calls;
}

/** A rank spoken on its own: deuces get a word, threes to tens a number. */
const boardRankClip = (rank: Rank): string =>
  rank === '2'
    ? 'holdem/rank_2'
    : rank === 'J' || rank === 'Q' || rank === 'K' || rank === 'A'
      ? `holdem/rank_${rank}`
      : `common/n_${Number(rank)}`;

/** The winning hand's name, as a clip. Null when no clip says it. */
const holdemLabelClip = (label: string): string | null => {
  if (label === 'Royal flush') return 'holdem/hand_royal_flush';
  if (label.endsWith('straight flush')) return 'common/hand_straight_flush';
  if (label.startsWith('Four of a kind')) return 'holdem/hand_four_of_a_kind';
  if (label.startsWith('Full house')) return 'holdem/hand_full_house';
  if (label.endsWith('flush')) return 'common/hand_flush';
  if (label.endsWith('straight')) return 'common/hand_straight';
  if (label.startsWith('Three of a kind')) return 'common/hand_three_of_a_kind';
  if (label.startsWith('Two pair')) return 'holdem/hand_two_pair';
  if (label.startsWith('Pair of')) return 'common/hand_pair';
  if (label.endsWith('high')) return 'common/hand_high_card';
  return null;
};

const STREET_CLIPS: Partial<Record<HoldemStreet, string>> = {
  flop: 'holdem/the_flop',
  turn: 'holdem/the_turn',
  river: 'holdem/the_river',
};

/** The spoken actions; blind and ante posts ride "Blinds are in." instead. */
const HOLD_EM_ACTION_CLIPS: Partial<Record<string, string>> = {
  fold: 'holdem/fold',
  check: 'holdem/check',
  call: 'holdem/call',
  bet: 'holdem/bet',
  raise: 'holdem/raise',
  all_in: 'holdem/all_in',
};

/** The Hold'em announcer: blinds, streets, the action, the pots. */
function holdemCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const h = next.holdem;
  const p = prev.holdem;
  if (!h || !p) return [];
  const calls: Callout[] = [];

  if (!p.street && h.street === 'preflop') {
    calls.push(call(`blinds:${next.round}`, ['holdem/blinds_in'], 'low', false));
  }

  if (h.street && p.street && h.street !== p.street) {
    const streetClip = STREET_CLIPS[h.street];
    if (streetClip) {
      const clips = [streetClip];
      if (mode === 'full') {
        // Read only the cards that just landed, not the whole board again.
        for (const rank of h.board.slice(p.board.length)) {
          if (rank) clips.push(boardRankClip(rank));
        }
      }
      calls.push(call(`street:${next.round}:${h.street}`, clips, 'normal', false));
    }
  }

  const actionClip = h.action ? HOLD_EM_ACTION_CLIPS[h.action] : null;
  if (actionClip && h.actionKey && h.actionKey !== p.actionKey) {
    // A raise or a shove decides the hand's size; the rest is colour.
    const spoken = h.action === 'raise' || h.action === 'all_in';
    if (spoken || mode === 'full') {
      calls.push(call(`act:${next.round}:${h.actionKey}`, [actionClip], spoken ? 'normal' : 'low', spoken));
    }
  }

  if (h.active === 2 && p.active > 2) {
    calls.push(call(`hu:${next.round}`, ['holdem/heads_up'], 'low', false));
  }

  if (h.results && !p.results) {
    const clips: string[] = [];
    if (!h.wonByFold && mode === 'full' && h.revealed) {
      clips.push('holdem/showdown');
    }
    const pots = h.results.filter((pot) => pot.winners.length > 0);
    const main = pots[0];
    if (!main || h.wonByFold) {
      // Everyone else folded, or the pot found no winner: take it down.
      clips.push('holdem/take_it_down');
    } else {
      if (mode === 'full' && pots.length > 1) clips.push('holdem/main_pot');
      clips.push(main.winners.length > 1 ? 'holdem/split_pot' : 'holdem/ship_it');
      const labelClip = main.label ? holdemLabelClip(main.label) : null;
      if (labelClip) clips.push(labelClip);
      if (mode === 'full') {
        pots.slice(1).forEach(() => clips.push('holdem/side_pot', 'holdem/ship_it'));
      }
    }
    calls.push(call(`pot:${next.round}`, clips, 'high', true));
  }

  return calls;
}

/** The three-card dealer's label, as a clip. Null when no clip says it. */
const threecardLabelClip = (label: string | null): string | null => {
  if (label === 'Mini royal') return 'threecard/mini_royal';
  if (label === 'Queen high') return 'threecard/queen_high';
  if (label === 'King high') return 'threecard/king_high';
  if (label === 'Ace high') return 'threecard/ace_high';
  return null;
};

/** The three-card caller: the decision, the reveal, the pays. */
function threecardCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const t = next.threecard;
  const p = prev.threecard;
  if (!t || !p) return [];
  const calls: Callout[] = [];

  if (prev.phase === 'betting' && next.phase === 'playing') {
    calls.push(call(`decide:${next.round}`, ['threecard/make_your_decision'], 'normal', true));
  }

  if (t.dealerQualifies !== null && p.dealerQualifies === null) {
    const clips = [t.dealerQualifies ? 'threecard/dealer_qualifies' : 'threecard/dealer_does_not_qualify'];
    const labelClip = mode === 'full' ? threecardLabelClip(t.dealerLabel) : null;
    if (labelClip) clips.push(labelClip);
    calls.push(call(`qualify:${next.round}`, clips, 'high', true));
  }

  if (t.settled && !p.settled) {
    if (t.dealerHeld) {
      calls.push(call(`held:${next.round}`, ['threecard/dealer_wins'], 'normal', true));
    }
    if (mode === 'full') {
      if (t.bonusPaid) calls.push(call(`bonus:${next.round}`, ['threecard/ante_bonus'], 'normal', false));
      if (t.pairPlusWon > p.pairPlusWon) {
        calls.push(call(`pp:${next.round}`, ['threecard/pair_plus_winner'], 'normal', false));
      }
    }
  }

  return calls;
}

/** The keno caller: the draw opens, a burst of numbers, the board's last word. */
function kenoCalls(prev: CalloutSnapshot, next: CalloutSnapshot, mode: CalloutMode): Callout[] {
  const k = next.keno;
  const p = prev.keno;
  if (!k || !p) return [];
  const calls: Callout[] = [];

  if (k.draw && !p.draw) {
    if (mode === 'full') {
      const clips = k.draw.slice(0, KENO_CALLED_NUMBERS).map((n) => `common/n_${n}`);
      if (clips.length) calls.push(call(`numbers:${next.round}`, clips, 'low', false));
      calls.push(call(`drawdone:${next.round}`, ['keno/thats_the_draw'], 'normal', false, KENO_TAIL_MS));
    }
  }

  if (k.settled && !p.settled) {
    if (k.solid) calls.push(call(`solid:${next.round}`, ['keno/solid_catch'], 'high', true));
    if (mode === 'full' && k.paid !== null) {
      calls.push(call(`paid:${next.round}`, [k.paid ? 'common/pay_the_winners' : 'common/no_winners'], 'low', false));
    }
  }

  return calls;
}

/**
 * The one call a clock earns before it runs out: "Last call for bets." on a
 * betting window, "Shooter, let's roll them." on the shooter's clock. Both are
 * `full` only. Null when there is no clock, the call's moment has already
 * gone, or the window is too short to be worth one.
 */
export function clockCallout(
  next: CalloutSnapshot | null,
  mode: CalloutMode,
  now: number,
): { callout: Callout; at: number } | null {
  if (mode !== 'full' || !next?.clock) return null;
  const at = next.clock.endsAt - CLOCK_CALL_MS;
  if (at <= now) return null;
  if (next.clock.kind === 'bet' && next.phase === 'betting') {
    return { callout: call(`lastcall:${next.round}`, ['common/last_call'], 'low', false), at };
  }
  if (next.clock.kind === 'action' && next.phase === 'rolling' && next.gameType === 'craps') {
    const roll = next.craps?.rollNumber ?? 0;
    return { callout: call(`nudge:${next.round}:${roll}`, ['craps/shooter_nudge'], 'low', false), at };
  }
  return null;
}
